import React, { useState, useEffect } from 'react';
import CustomerLayout from '../../components/layout/CustomerLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { customerService } from '../../services/customerService';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiRepeat
} from 'react-icons/fi';

const CustomerPaymentSummary = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await customerService.getPaymentSummary();
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error loading payment summary:', error);
      setError('Failed to load payment summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomerLayout title="Payment Summary">
        <LoadingSpinner fullScreen />
      </CustomerLayout>
    );
  }

  if (error) {
    return (
      <CustomerLayout title="Payment Summary">
        <div className="text-center text-red-600">{error}</div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout title="Payment Summary">
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Total Outstanding */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-button flex items-center justify-center">
                <FiTrendingUp className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-neutral-600 mb-1">
              Current Outstanding
            </h3>
            <p className="text-3xl font-bold text-red-600">
              {summary?.totalOutstanding?.toFixed(3)} BHD
            </p>
          </div>

          {/* Total Paid */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-button flex items-center justify-center">
                <FiTrendingDown className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-neutral-600 mb-1">
              Total Paid
            </h3>
            <p className="text-3xl font-bold text-green-600">
              {summary?.totalPaid?.toFixed(3)} BHD
            </p>
          </div>

          {/* Payment Plan */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-button flex items-center justify-center">
                <FiRepeat className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-neutral-600 mb-1">
              Payment Plan
            </h3>
            <p className="text-2xl font-bold text-neutral-900 capitalize">
              {summary?.paymentPlan || 'None'}
            </p>
          </div>
        </div>

        {/* Detailed Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Debt Statistics */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center">
              <FiTrendingUp className="w-5 h-5 text-red-600 mr-2" />
              Debt Statistics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Total Debt Amount</span>
                <span className="text-lg font-bold text-red-600">
                  {summary?.totalDebt?.toFixed(3)} BHD
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Number of Debts</span>
                <span className="text-lg font-bold text-neutral-900">
                  {summary?.debtTransactions || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Average Debt</span>
                <span className="text-lg font-bold text-neutral-900">
                  {summary?.debtTransactions > 0
                    ? (summary.totalDebt / summary.debtTransactions).toFixed(3)
                    : '0.000'} BHD
                </span>
              </div>
            </div>
          </div>

          {/* Payment Statistics */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center">
              <FiTrendingDown className="w-5 h-5 text-green-600 mr-2" />
              Payment Statistics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Total Payments Made</span>
                <span className="text-lg font-bold text-green-600">
                  {summary?.totalPayments?.toFixed(3)} BHD
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Number of Payments</span>
                <span className="text-lg font-bold text-neutral-900">
                  {summary?.paymentTransactions || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Average Payment</span>
                <span className="text-lg font-bold text-neutral-900">
                  {summary?.paymentTransactions > 0
                    ? (summary.totalPayments / summary.paymentTransactions).toFixed(3)
                    : '0.000'} BHD
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="bg-white rounded-card shadow-card p-6">
          <h3 className="text-lg font-bold text-neutral-900 mb-4">Payment Progress</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-neutral-600">Paid</span>
                <span className="font-medium text-neutral-900">
                  {summary && summary.totalDebt > 0
                    ? ((summary.totalPaid / summary.totalDebt) * 100).toFixed(1)
                    : '0'}%
                </span>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-4">
                <div
                  className="bg-green-600 h-4 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      summary && summary.totalDebt > 0
                        ? Math.min((summary.totalPaid / summary.totalDebt) * 100, 100)
                        : 0
                    }%`
                  }}
                ></div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-neutral-200">
              <div>
                <p className="text-sm text-neutral-600">Total Debt</p>
                <p className="text-lg font-bold text-neutral-900">
                  {summary?.totalDebt?.toFixed(3)} BHD
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Paid</p>
                <p className="text-lg font-bold text-green-600">
                  {summary?.totalPaid?.toFixed(3)} BHD
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Remaining</p>
                <p className="text-lg font-bold text-red-600">
                  {summary?.totalOutstanding?.toFixed(3)} BHD
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerPaymentSummary;
